import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { examsApi, ExamType, CreateExamTypeDto } from '../../api/exams';

interface Props {
  isDark: boolean;
}

const empty: CreateExamTypeDto & { config_text: string } = {
  slug: '',
  name: '',
  description: '',
  time_limit_minutes: undefined,
  config: undefined,
  config_text: '',
};

export default function ExamTypesPage({ isDark }: Props) {
  const [items, setItems] = useState<ExamType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ExamType | null>(null);
  const [form, setForm] = useState(empty);

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await examsApi.getExamTypes();
      setItems(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch exam types');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const handleEdit = (et: ExamType) => {
    setEditing(et);
    setForm({
      slug: et.slug,
      name: et.name,
      description: et.description || '',
      time_limit_minutes: et.time_limit_minutes ?? undefined,
      config: et.config || undefined,
      config_text: et.config ? JSON.stringify(et.config, null, 2) : '',
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    let parsedConfig: Record<string, any> | undefined;
    if (form.config_text.trim()) {
      try {
        parsedConfig = JSON.parse(form.config_text);
      } catch {
        toast.error('Config must be valid JSON');
        return;
      }
    }

    try {
      if (editing) {
        await examsApi.updateExamType(editing.id, {
          name: form.name,
          description: form.description || null,
          time_limit_minutes: form.time_limit_minutes ?? null,
          config: parsedConfig ?? null,
        });
        toast.success('Exam type updated');
      } else {
        await examsApi.createExamType({
          slug: form.slug,
          name: form.name,
          description: form.description || null,
          time_limit_minutes: form.time_limit_minutes ?? null,
          config: parsedConfig ?? null,
        });
        toast.success('Exam type created');
      }
      setOpen(false);
      fetchItems();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save exam type');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this exam type?')) return;
    try {
      await examsApi.deleteExamType(id);
      toast.success('Exam type deleted');
      fetchItems();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const inputCls = cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200');
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');
  const headCls = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            Exam Types
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            DTM, Sertifikat, Mavzuli — manage exam type configuration
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" /> New Exam Type
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                <TableHead className={headCls}>Slug</TableHead>
                <TableHead className={headCls}>Name</TableHead>
                <TableHead className={headCls}>Time limit</TableHead>
                <TableHead className={headCls}>Config</TableHead>
                <TableHead className={headCls}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    No exam types
                  </TableCell>
                </TableRow>
              ) : (
                items.map((et) => (
                  <TableRow
                    key={et.id}
                    className={cn(isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50')}
                  >
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {et.slug}
                      </Badge>
                    </TableCell>
                    <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>
                      {et.name}
                    </TableCell>
                    <TableCell className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>
                      {et.time_limit_minutes ? `${et.time_limit_minutes} min` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {et.config ? `${Object.keys(et.config).length} keys` : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(et)} className="h-8 px-2">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(et.id)}
                          className="h-8 px-2 text-slate-500 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={cn(
            'max-w-2xl max-h-[90vh] overflow-y-auto',
            isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
          )}
        >
          <DialogHeader>
            <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
              {editing ? 'Edit exam type' : 'Create exam type'}
            </DialogTitle>
            <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
              Configure exam type settings (config is JSON: e.g. mandatory subjects, scoring).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                  disabled={!!editing}
                  placeholder="dtm"
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Time limit (minutes)</Label>
                <Input
                  type="number"
                  value={form.time_limit_minutes ?? ''}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      time_limit_minutes: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                  className={inputCls}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Description</Label>
              <Textarea
                value={form.description || ''}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={3}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Config (JSON)</Label>
              <Textarea
                value={form.config_text}
                onChange={(e) => setForm((p) => ({ ...p, config_text: e.target.value }))}
                rows={6}
                placeholder='{ "total_questions": 90 }'
                className={cn('font-mono text-xs', inputCls)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
              {editing ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
